import { computed, ref } from "vue";

const PRESENTATION_MODE_COOKIE = "neptune_presentation_mode";
const PRESENTATION_MODE_COOKIE_MAX_AGE = 60 * 60 * 24 * 365;
const POUND_AMOUNT_PATTERN = /-?£\s?\d[\d,]*(?:\.\d+)?/g;

const enabled = ref(false);
let initialized = false;
let numberFormatPatched = false;

const readCookie = () => {
  if (typeof document === "undefined") {
    return false;
  }

  const cookieEntry = document.cookie
    .split("; ")
    .find((entry) => entry.startsWith(`${PRESENTATION_MODE_COOKIE}=`));

  if (!cookieEntry) {
    return false;
  }

  const value = cookieEntry.substring(PRESENTATION_MODE_COOKIE.length + 1);

  return value === "1";
};

const writeCookie = (value) => {
  if (typeof document === "undefined") {
    return;
  }

  const normalized = value ? "1" : "0";
  document.cookie = `${PRESENTATION_MODE_COOKIE}=${normalized}; path=/; max-age=${PRESENTATION_MODE_COOKIE_MAX_AGE}; samesite=lax`;
};

const initializeCookieState = () => {
  if (initialized) {
    return;
  }

  enabled.value = readCookie();
  initialized = true;
};

export const maskPoundAmount = (value) => {
  const raw = String(value ?? "");
  const match = raw.match(/^(-?)£(\s?)\d[\d,]*(?:\.\d+)?$/);
  if (!match) {
    return raw;
  }

  if (!enabled.value) {
    return raw;
  }

  return `${match[1]}£${match[2]}#####`;
};

export const maskPoundValuesInText = (value) => {
  const raw = String(value ?? "");
  return raw.replace(POUND_AMOUNT_PATTERN, (match) => maskPoundAmount(match));
};

const installNumberFormatPatch = () => {
  if (numberFormatPatched || typeof Intl === "undefined" || !Intl.NumberFormat) {
    return;
  }

  const descriptor = Object.getOwnPropertyDescriptor(
    Intl.NumberFormat.prototype,
    "format",
  );

  if (!descriptor?.get) {
    return;
  }

  const originalGetFormat = descriptor.get;

  Object.defineProperty(Intl.NumberFormat.prototype, "format", {
    ...descriptor,
    get() {
      const baseFormatter = originalGetFormat.call(this);
      const options = this?.resolvedOptions?.();
      const isGbpCurrency =
        options?.style === "currency" && options?.currency === "GBP";

      if (!isGbpCurrency) {
        return baseFormatter;
      }

      return (value) => {
        const formatted = baseFormatter(value);
        return maskPoundValuesInText(formatted);
      };
    },
  });

  numberFormatPatched = true;
};

export const initializePresentationMode = () => {
  initializeCookieState();
  installNumberFormatPatch();
};

export const usePresentationMode = () => {
  initializeCookieState();

  const isPresentationModeEnabled = computed(() => enabled.value);
  const presentationModeEnabled = computed({
    get: () => enabled.value,
    set: (value) => {
      enabled.value = Boolean(value);
      writeCookie(enabled.value);
    },
  });

  return {
    isPresentationModeEnabled,
    presentationModeEnabled,
    setPresentationMode: (value) => {
      enabled.value = Boolean(value);
      writeCookie(enabled.value);
    },
    maskPoundAmount,
    maskPoundValuesInText,
  };
};
